// Recovery action cho scenario "folder code clone từ team khác, user không có
// quyền push lên repo gốc nhưng muốn dùng folder làm khởi đầu cho project mới
// của mình".
//
// Flow:
//   1. Backup .git → .git.backup-{YYMMDD-HHMM} (safe undo nếu nhầm)
//   2. git init mới trên folder
//   3. Optional: gh repo create dưới owner mới (current gh user hoặc org)
//   4. Trả URL mới để caller dùng cho submodule add
//
// User trigger qua menu trong handleRemoteAccessFailureWithAccountSwitch khi
// detect "no-access" trên existing-folder flow.

import { spawnSync } from "node:child_process";
import { promises as fs } from "node:fs";
import { basename, join } from "node:path";
import { createGithubRemoteFromFolder } from "./create-github-remote-from-folder.js";
import { pathExists } from "./filesystem-helpers.js";
import { getActiveFlag } from "./non-interactive-context.js";
import { confirmOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import { log } from "./terminal-logger.js";
import type { RepoVisibility } from "./validate-repo-name-and-visibility.js";

// Timestamp YYMMDD-HHMM cho backup folder — readable + sortable.
function backupTimestamp(): string {
  const d = new Date();
  return (
    `${d.getFullYear().toString().slice(-2)}` +
    `${String(d.getMonth() + 1).padStart(2, "0")}` +
    `${String(d.getDate()).padStart(2, "0")}-` +
    `${String(d.getHours()).padStart(2, "0")}` +
    `${String(d.getMinutes()).padStart(2, "0")}`
  );
}

export interface ResetFolderResult {
  newRemoteUrl: string; // HTTPS URL của repo mới
  backupPath: string; // Path tới .git.backup-{stamp}
}

// Backup .git → .git.backup-{stamp} thay vì xóa hẳn.
// User restore được nếu nhầm: `mv .git.backup-XXX .git`.
async function backupExistingDotGit(folderPath: string): Promise<string> {
  const gitDir = join(folderPath, ".git");
  if (!(await pathExists(gitDir))) {
    throw new Error(`.git không tồn tại ở ${folderPath} — không cần reset.`);
  }
  const backupName = `.git.backup-${backupTimestamp()}`;
  const backupPath = join(folderPath, backupName);
  await fs.rename(gitDir, backupPath);
  log.success(`Backup .git → ${backupName}`);
  return backupPath;
}

// git init + add all + initial commit để folder ready cho gh repo create.
function reinitGitInFolder(folderPath: string): void {
  const r1 = spawnSync("git", ["-C", folderPath, "init", "-b", "main"], {
    encoding: "utf8",
    stdio: ["ignore", "pipe", "pipe"],
  });
  if (r1.status !== 0) {
    throw new Error(`git init thất bại: ${r1.stderr || r1.stdout}`);
  }
  log.success("Git init mới (branch main)");

  // Stage all + commit để gh repo create có thể push.
  const rAdd = spawnSync("git", ["-C", folderPath, "add", "-A"], {
    encoding: "utf8",
    stdio: ["ignore", "pipe", "pipe"],
  });
  if (rAdd.status !== 0) {
    // add fail → commit chắc chắn rỗng → remote sẽ rỗng dù folder có file.
    // Warn rõ thay vì để silent (trước đây bỏ qua result hoàn toàn).
    log.warn(`git add -A thất bại: ${(rAdd.stderr || "").slice(0, 200)}`);
  }
  const r3 = spawnSync(
    "git",
    ["-C", folderPath, "commit", "-m", "chore: initial commit from existing folder"],
    { encoding: "utf8", stdio: ["ignore", "pipe", "pipe"] },
  );
  if (r3.status !== 0) {
    // Commit fail OK nếu folder rỗng — log warn không throw.
    log.warn(`git commit thất bại (folder có thể rỗng): ${(r3.stderr || "").slice(0, 200)}`);
  } else {
    log.success("Initial commit");
  }
}

// Main entry: backup .git + reinit + tạo remote mới + return URL.
//
// repoName: default = basename(folderPath). Caller có thể override qua opts.
// autoYes=true: skip mọi confirm (CI mode) — visibility default "private".
const RESET_GIT_FLAG_SPEC = {
  flag: "--reset-git",
  command: "add repo",
  hint: "--reset-git yes|no",
};
const NEW_VISIBILITY_FLAG_SPEC = {
  flag: "--new-remote-visibility",
  command: "add repo",
  hint: "--new-remote-visibility private|public",
};

export async function resetFolderGitAndCreateNewRemoteUnderCurrentUser(opts: {
  folderPath: string;
  repoName?: string;
  visibility?: RepoVisibility;
  org?: string; // default = current gh user
  autoYes?: boolean;
}): Promise<ResetFolderResult> {
  const folderName = basename(opts.folderPath);
  const repoName = opts.repoName ?? folderName;

  // Confirm trước khi destructive op — clear UX về backup behavior.
  if (!opts.autoYes) {
    const confirmed = await confirmOrFlag(
      getActiveFlag("resetGit") === undefined ? undefined : getActiveFlag("resetGit") === "yes",
      RESET_GIT_FLAG_SPEC,
      {
        message: `Folder '${folderName}' sẽ được reset:\n  1. Backup .git → .git.backup-{timestamp}\n  2. Git init mới (branch main, initial commit)\n  3. Tạo GitHub repo mới '${repoName}' dưới account của bạn\nTiếp tục?`,
        default: true,
      },
    );
    if (!confirmed) {
      throw new Error("User abort reset folder.");
    }
  }

  const visibility =
    opts.visibility ??
    (opts.autoYes
      ? "private"
      : ((await selectOrFlag(getActiveFlag("newRemoteVisibility"), NEW_VISIBILITY_FLAG_SPEC, {
          message: "Visibility cho repo mới?",
          choices: [
            { name: "private (mặc định)", value: "private" as const },
            { name: "public", value: "public" as const },
          ],
        })) as RepoVisibility));

  // Step 1: Backup .git.
  const backupPath = await backupExistingDotGit(opts.folderPath);

  // Step 2: Reinit + commit.
  reinitGitInFolder(opts.folderPath);

  // Step 3: Tạo remote qua gh + push initial commit.
  const urls = createGithubRemoteFromFolder({
    folder: opts.folderPath,
    name: repoName,
    visibility,
    org: opts.org,
  });

  return {
    newRemoteUrl: urls.httpsUrl,
    backupPath,
  };
}
